import React, { useState, useRef, useEffect } from 'react';
import Modal from 'common/components/modules/modal';
import Button from 'common/components/elements/buttons/primary';
import PropTypes from 'prop-types';
import PageHeader from 'common/components/modules/page-header';
import PageLoader from 'common/components/modules/page-loading';
import Icon from 'common/components/elements/icons';
import AmountChip from '../amount-chip';
import GenerateColor from 'common/utils/generateTextColor.js';
import NumberFormatter from 'common/utils/NumberFormatter';
import './style.scss';
import Checkbox from 'common/components/elements/inputs/checkbox-label';
import Switch from 'common/components/elements/inputs/switch';
import Input from 'common/components/elements/inputs/inputs';
import IntercomMoreInfo, { MORE_INFO_ON_WALLET_AND_FEES } from 'sites/utils/IntercomMoreInfo';
import cx from 'classnames';
import toast from 'common/utils/toast';

const numberFormatter = new NumberFormatter();

const WalletModal = ({
   onModalClose,
   selectedAmount,
   onSelectAmount,
   topUpMyWallet,
   type,
   amounts,
   amountSettings,
   isMobile,
   height,
   onClickButton,
   isFetching,
   balance,
   isFetchingWallet,
   walletData,
   animate,
   isProgressing,
   isNotEnoughtBalance,
   goToWalletTopUp,
   hasPageHeader,
   chatSendTextPrice,
   modalPosition,
   memberUuid,
   adminScreenName,
   isAddProcessingFees,
   onChangeAddProcessingFees,
   isFetchingBalance,
   paymentButtonLoading,
   extraInfo,
   tipContentData,
   setSendPurchase,
   siteOwnerName,
}) => {
   const showModal = localStorage.getItem('dont_show_chat_text_pay_modal_24_hours') ? JSON.parse(localStorage.getItem('dont_show_chat_text_pay_modal_24_hours')) : {}

   const [chatTextMessageDontShowModal, setChatTextMessageDontShowModal] = useState(!!showModal[memberUuid])
   const [noteText, setNoteText] = useState('')
   const [isCustomTipError, setIsCustomTipError] = useState(false);
   const [useIsCustomTip, setUseIsCustomTip] = useState(false);

   const modalHeight = isMobile ? 'calc(100% - 58px)' : height > 850 ? '810px' : height - 100 + 'px';
   const yourBalance = (
      <div className='flex flex-col items-center border-b border-divider pb-5 dark:border-divider-dark'>
         <div
            style={ {
               background: GenerateColor.generateRGBColor(window.skinSecondaryColor, 0.08),
            } }
            className='w-11 h-11 rounded-full flex items-center justify-center mb-3'>
            <Icon name='wallet' primaryColor={ window.skinSecondaryColor } size='2xl' />
         </div>
         <span
            className='text-sm text-secondary dark:text-secondary-dark '
            style={ { lineHeight: '16px' } }
         >
            YOUR BALANCE IS {numberFormatter.withDots(balance)} USD
         </span>
      </div>
   )

   const payPayedMessage = ['chat_text_message', 'chat_voice_message', 'chat_photo_message'].includes(type);

   const contentView = {
      tip: {
         buttonText: `Tip ${ selectedAmount ? selectedAmount + ' USD' : '' }`,
         buttonDisabled: !selectedAmount || isFetching,
         buttonLoading: isFetchingBalance,
         component: <Tip
            amounts={ amounts }
            amountSettings={ amountSettings }
            selectedAmount={ selectedAmount }
            onSelectAmount={ onSelectAmount }
            isFetching={ isFetching }
            topUpMyWallet={ topUpMyWallet }
            balance={ balance }
            extraInfo={ extraInfo }
            setNoteText={ setNoteText }
            noteText={ noteText }
            siteOwnerName={ siteOwnerName }
            setIsCustomTipError={ setIsCustomTipError }
            setUseIsCustomTip={ setUseIsCustomTip }
            useIsCustomTip={ useIsCustomTip }
            isCustomTipError={ isCustomTipError }
         />,
      },
      top_up: {
         buttonText: 'Top up my wallet',
         buttonDisabled: !selectedAmount || isFetchingWallet,
         buttonLoading: isFetchingBalance,
         showBalance: true,
         component: <TopUp
            amounts={ walletData }
            selectedAmount={ selectedAmount }
            onSelectAmount={ onSelectAmount }
            isFetching={ isFetchingWallet }
            isAddProcessingFees={ isAddProcessingFees }
            onChangeAddProcessingFees={ onChangeAddProcessingFees }
            balance={ balance }
         />,
      },
      chat_text_message:
      {
         buttonText: 'Pay and send',
         buttonDisabled: isFetching,
         showBalance: true,
         component: (
            <ChatMessage
               chatSendTextPrice={ chatSendTextPrice }
               type={ type }
               setChatTextMessageDontShowModal={ setChatTextMessageDontShowModal }
               chatTextMessageDontShowModal={ chatTextMessageDontShowModal }
            />
         ),
      },
      chat_voice_message: {
         buttonText: 'Pay and send',
         buttonDisabled: isFetching,
         showBalance: true,
         component: (
            <ChatMessage
               chatSendTextPrice={ chatSendTextPrice }
               type={ type }
               setChatTextMessageDontShowModal={ setChatTextMessageDontShowModal }
               chatTextMessageDontShowModal={ chatTextMessageDontShowModal }
            />
         ),
      },
      chat_photo_message: {
         buttonText: 'Pay and send',
         buttonDisabled: isFetching,
         showBalance: true,
         component: (
            <ChatMessage
               chatSendTextPrice={ chatSendTextPrice }
               type={ type }
               setChatTextMessageDontShowModal={ setChatTextMessageDontShowModal }
               chatTextMessageDontShowModal={ chatTextMessageDontShowModal }
            />
         ),
      },
   }
   let modalData = contentView[type] || {}
   return (
      <Modal
         contentPosition={ isMobile ? modalPosition : 'center' }
         onClose={ onModalClose }
         contentWidth={ isMobile ? '100%' : '375px' }
         animate={ isMobile ? animate : null }
         classNames={ `h-full ${ modalPosition === 'full-screen' ? 'wallet-full-screen-modal' : '' }` }
         isCloseAction={ false }
      >
         {
            hasPageHeader && (
               <PageHeader
                  title={ type === 'tip' ? 'Tip' : 'Wallet top up' }
                  fontIconSize='doubleLarge'
                  fontIconColor='major'
                  darkcolor='major-dark'
                  leftIconName='close'
                  truncateHeaderText={ false }
                  leftIconAction={ onModalClose }
                  isFixed={ isMobile }
                  className='notranslate'
                  topHelpIconTooltip
               />
            )
         }
         <div
            className={ cx({
               'relative flex flex-col w-full overflow-y-auto select-none': true,
               'pb-5': type === 'tip' && hasPageHeader,
               'p-5': type !== 'tip' && type !== 'top_up',
               'pt-5': type === 'top_up',
               'wallet-amount-modal': modalPosition === 'full-screen',
            }) }
            style={ {
               maxHeight: isMobile ? 'calc(100% - 61px)' : modalHeight,
            } }
         >
            {
               !hasPageHeader && (
                  <div className='w-6 h-6 absolute left-0 top-0 ml-3 mt-3'>
                     <Button
                        iconName='close'
                        backgroundColor='transparent'
                        classNames='w-6 h-6'
                        padding='none'
                        fontIconColor='secondary dark:text-secondary-dark'
                        waveColor='rgba(106, 106, 106, 0.8)'
                        onClick={ onModalClose }
                     />
                  </div>
               )
            }
            {modalData.showBalance && yourBalance}
            {
               isNotEnoughtBalance && (
                  <div className='mx-1 pb-1'>
                     {
                        chatSendTextPrice < 4 && payPayedMessage && (
                           <span
                              className='text-base text-secondary dark:text-secondary-dark leading-tight mt-4 block'>
                              Items that cost less than 4.00 USD can be purchased only using the <span className='font-medium'>Wallet</span>
                              {/* <span
                                 className='cursor-pointer ml-px inline-flex items-center justify-center w-5 h-5 more-info-on-wallet' >
                                 <Icon name='help-outline' color='secondary' darkColor='secondary-dark'
                                    style={ {
                                       height: '16.67px',
                                    } }
                                 />
                              </span> */}
                           </span>
                        )
                     }
                     <div className='flex flex-col justify-center items-center cursor-pointer mt-4'>
                        <div className='flex flex-col items-center justify-center mt-2'>
                           <p
                              className={ `text-base text-secondary dark:text-secondary-dark ${ payPayedMessage ? 'font-medium mt-2' : '' }` }
                              style={ { lineHeight: '20px' } }
                           >
                              {
                                 payPayedMessage && (
                                    <>
                                       Text messages to  {adminScreenName} cost <span className=''>{numberFormatter.withDots(chatSendTextPrice)} USD</span>. Top up your wallet to send messages.
                                    </>
                                 )
                              }
                              {
                                 !payPayedMessage && 'Your wallet balance is insufficent to make this purchase. Please top up your wallet to continue.'
                              }

                           </p>
                        </div>
                        <div className='mt-10 h-9 w-full'>
                           <Button
                              text='More info on wallet and fees'
                              backgroundColor='transparent'
                              textSize='base'
                              iconName='attention-outline'
                              iconPosition='left'
                              fontIconSize='base'
                              primaryColor
                              onClick={ () => { IntercomMoreInfo(MORE_INFO_ON_WALLET_AND_FEES) } }
                              classNames='h-full w-full px-3 intercom-more-info-on-wallet-and-fees'
                           />
                        </div>

                        <div className='mt-8 h-12 w-full'>
                           <Button
                              text='Go to wallet top up'
                              textSize='base'
                              padding='none'
                              classNames='h-full w-full px-3'
                              onClick={ goToWalletTopUp }
                              primaryColor
                              borderRadius='large'
                              backgroundColor='transparent'
                           />
                        </div>
                     </div>
                  </div>
               )
            }
            {
               !isNotEnoughtBalance && (
                  <>
                     {
                        modalData.component && modalData.component
                     }
                     <div className={ `${ (type === 'tip' || type === 'top_up') ? 'px-5 pb-2' : '' }` }>
                        {
                           isProgressing && (
                              <span className='block text-center uppercase line-height-normal text-sm text-secondary dark:text-secondary-dark '>Processing payment...</span>
                           )
                        }
                        <div className='h-12 w-full mt-3'>
                           <Button
                              text={ modalData.buttonText }
                              textSize='base'
                              iconPosition='left'
                              fontIconColor='action'
                              fontIconSize='base'
                              primaryColor
                              classNames='w-auto h-full'
                              padding='none'
                              disabled={ modalData.buttonDisabled }
                              borderRadius='large'
                              onClick={ () => {
                                 if('tip' === type && amountSettings?.min_amount && amountSettings.max_amount){
                                    if(selectedAmount < amountSettings.min_amount || selectedAmount > amountSettings.max_amount){
                                       let toastMessage = `Minimum tip amount is ${ amountSettings.min_amount } USD`;
                                       if(selectedAmount > amountSettings.max_amount){
                                          toastMessage = `Single payment amount can't be higher than ${ amountSettings.max_amount } USD`;
                                       }
                                       if(useIsCustomTip){
                                          setIsCustomTipError(true);
                                       }
                                       toast.error(toastMessage);
                                       return
                                    }
                                 }
                                 if(payPayedMessage) {
                                    let res = 1
                                    if(!chatTextMessageDontShowModal) {
                                       res = 0
                                    }
                                    if(res) {
                                       let jsObject = {}
                                       if(localStorage.getItem('dont_show_chat_text_pay_modal_24_hours')) {
                                          jsObject = JSON.parse(localStorage.getItem('dont_show_chat_text_pay_modal_24_hours'))
                                       }
                                       const item = {
                                          ...jsObject,
                                          [`${ memberUuid }`]: {
                                             value: res,
                                             expiry: new Date().getTime() + 86400000,
                                          },
                                       }

                                       localStorage.setItem('dont_show_chat_text_pay_modal_24_hours', JSON.stringify(item))
                                    }
                                 }
                                 onClickButton(selectedAmount, extraInfo)
                                 if(setSendPurchase){
                                    setSendPurchase({ contentData: { ...tipContentData, message: noteText } })
                                 }
                              } }
                              isLoading={ 'tip' === type ? paymentButtonLoading : isProgressing || modalData.buttonLoading }
                           />
                        </div>
                        {
                           type === 'top_up' && (
                              <div className=' items-center w-full my-2'>
                                 <div
                                    style={ {
                                       width: '260px',
                                    } }
                                    className='h-9 flex items-center m-auto min-w-fit'>
                                    <Button
                                       text='More info on wallet and fees'
                                       backgroundColor='transparent'
                                       textSize='base'
                                       iconName='attention-outline'
                                       iconPosition='left'
                                       fontIconSize='base'
                                       padding='none'
                                       primaryColor
                                       onClick={ () => { IntercomMoreInfo(MORE_INFO_ON_WALLET_AND_FEES) } }
                                       classNames='intercom-more-info-on-wallet-and-fees'
                                    />
                                 </div>
                              </div>
                           )
                        }
                     </div>
                  </>
               )
            }

         </div>
      </Modal>
   );
};

const ChatMessage = ({
   chatSendTextPrice,
   setChatTextMessageDontShowModal,
   chatTextMessageDontShowModal,
   type,
}) => {
   const payPayedMessage = ['chat_text_message', 'chat_voice_message', 'chat_photo_message'].includes(type);
   const getMessageType = () => {
      let messageType = ''
      switch (type) {
         case 'chat_text_message':
            messageType = 'text'
            break;
         case 'chat_voice_message':
            messageType = 'voice'
            break;
         case 'chat_photo_message':
            messageType = 'photo'
            break;

         default:
            break;
      }
      return messageType
   }

   return (
      <>
         {
            chatSendTextPrice < 4 && payPayedMessage && (
               <span
                  className='text-base text-secondary dark:text-secondary-dark leading-tight mt-4 block'>
                  Items that cost less than 4.00 USD can be purchased only using the <span className='font-medium'>Wallet.</span>
                  <span
                     className='cursor-pointer ml-px inline-flex items-center justify-center w-5 h-5 more-info-on-wallet' >
                     <Icon name='help-outline' color='secondary' darkColor='secondary-dark'
                        style={ {
                           height: '16.67px',
                        } }
                     /></span>
               </span>
            )
         }
         <div className='flex flex-col items-center mt-10 mb-7'>
            <span className='text-xl text-major dark:text-major-dark'>{numberFormatter.withDots(chatSendTextPrice)} USD </span>
            <span className='text-center text-secondary leading-tight mt-3 dark:text-secondary-dark '>Pay {numberFormatter.withDots(chatSendTextPrice)} USD from your wallet to send your message </span>
            <Checkbox
               name='content_policy_agreement'
               onChange={ (name, value) => {
                  let res = 1
                  if(!value) {
                     res = 0
                  }
                  setChatTextMessageDontShowModal(!!res);
               } }
               className='border border-divider justify-between rounded-lg mt-10 dark:border-divider-dark'
               checked={ chatTextMessageDontShowModal }
               position='left'
            >
               <div className='text-major flex-1 flex flex-col dark:text-major-dark'>
                  <span className='text-major leading-tight dark:text-major-dark'>Don't show this warning for 24 hours</span>
                  <span className='text-xs text-secondary leading-none mt-2 dark:text-secondary-dark '>Subsequent {getMessageType()} messages will cost {numberFormatter.withDots(chatSendTextPrice)} USD</span>
               </div>
            </Checkbox>
         </div>
      </>
   )
}

const Tip = ({
   amounts,
   selectedAmount,
   onSelectAmount,
   isFetching,
   topUpMyWallet,
   balance,
   extraInfo,
   setNoteText,
   noteText,
   siteOwnerName,
   amountSettings,
   setIsCustomTipError,
   isCustomTipError,
   setUseIsCustomTip,
   useIsCustomTip,
}) => {

   const isLiveStreamTip = extraInfo && extraInfo?.tip_type === 'live_stream';
   const action = extraInfo?.tip_action;

   const [customTip, setCustomTip] = useState(null);
   const inputRef = useRef(null);
   const getMaxPrice = () => {
      if(!amountSettings.max_amount){
         return 9999;
      }
      const maxAmountToString = Math.round(amountSettings.max_amount).toString();
      let mexPriceLength = maxAmountToString.length;
      const ch = '9';
      let res = Number(ch.repeat(mexPriceLength));
      return res;
   }
   useEffect(() => {
      if(useIsCustomTip && inputRef?.current){
         inputRef.current.focus()
      }
   }, [useIsCustomTip, inputRef])

   if(isFetching) {
      return <PageLoader withLoader customStyle='h-full w-full my-10' loaderColor='primary' />
   }
   return (
      <div className='flex flex-col select-none '>
         <div className='p-2 flex items-center justify-between border-b border-divider dark:border-divider-dark'>
            <div className='flex flex-row items-center'>
               <div
                  style={ {
                     background: GenerateColor.generateRGBColor(window.skinSecondaryColor, 0.08),
                  } }
                  className='w-10 h-10 rounded-full flex items-center justify-center'>
                  <Icon name='wallet' primaryColor={ window.skinSecondaryColor } size='2xl' />
               </div>
               <div className='ml-4'>
                  <span className='text-base font-medium leading-tight dark:text-major-dark'>
                     Wallet •
                     <span className='text-base font-medium leading-tight text-placeholder dark:text-placeholder-dark'>  {numberFormatter.withDots(balance)} USD </span>

                  </span>
               </div>
            </div>
            <div className='min-w-fit'>
               <Button
                  text='Top up'
                  textSize='base'
                  padding='none'
                  classNames='h-full w-full flex justify-center px-3 rounded-lg py-2'
                  backgroundColor='transparent'
                  primaryColor
                  onClick={ () => topUpMyWallet(selectedAmount) }
               />
            </div>
         </div>
         {
            isLiveStreamTip && !!action ? (
               <div className='flex flex-col items-center py-5 px-3'>
                  <span className='text-xl'>{action.emoji}</span>
                  <span className='text-2xl text-major dark:text-major-dark mt-6'>{action.price} USD</span>
                  <span className='text-base leading-tight text-secondary dark:text-secondary-dark mt-2'>Tip <span className='font-bold'>{action.price} USD</span> for <span className='font-bold'>{action.name}</span> to {siteOwnerName}</span>
               </div>
            ) : (

               <div className='py-5 px-3'>
                  <div className='flex justify-center pt-1'>
                     <p
                        className='text-center text-2xl dark:text-major-dark'
                        style={ { lineHeight: '28px', maxWidth: '255px' } }
                     >
                        Select tip amount:
                     </p>
                  </div>
                  <div className='flex flex-wrap mt-6 mb-2'>
                     {
                        amounts && amounts.map((amount, index) => {
                           return (
                              <AmountChip
                                 amount={ amount }
                                 key={ index.toString() }
                                 selected={ !useIsCustomTip && selectedAmount === amount }
                                 onSelectAmount={ () => {
                                    setUseIsCustomTip(false);
                                    onSelectAmount(amount);
                                    if(isCustomTipError){
                                       setIsCustomTipError(false)
                                    }
                                 } }
                              />
                           )
                        })
                     }


                     <AmountChip
                        selected={ useIsCustomTip }
                        onSelectAmount={ () => {
                           setUseIsCustomTip(true);
                           onSelectAmount(customTip);
                           if(isCustomTipError){
                              setIsCustomTipError(false)
                           }
                           if(useIsCustomTip && inputRef?.current){
                              inputRef.current.focus();
                           }
                        } }
                        isError={ isCustomTipError }
                        isShowInput={ useIsCustomTip }
                     >
                        {
                           !useIsCustomTip && (
                              <span className='text-secondary dark:text-secondary-dark text-base leading-[18px]'>Custom tip</span>
                           )
                        }
                        {
                           useIsCustomTip && (
                              <Input
                                 inputRef={ inputRef }
                                 className='price-input-styles custom-tip-input'
                                 rightText='USD'
                                 pattern='d\*'
                                 type='number'
                                 value={ customTip }
                                 name='custom_tip'
                                 onKeyPress={ e => {
                                    let regex = /[0-9]|[.]$/;
                                    if(!regex.test(e.nativeEvent.key)) {
                                       e.preventDefault()
                                       return
                                    }
                                 } }
                                 maxPrice={ getMaxPrice() }
                                 isPriceInput
                                 onChange={ (name, value) => {
                                    onSelectAmount(value);
                                    setCustomTip(value);
                                 } }
                                 height='10'
                              />
                           )
                        }
                     </AmountChip>

                     {
                        <div className='h-12 w-full mt-3 mx-2 mb-4'>
                           <label className='dark:text-major-dark'>
                              Note
                              <input
                                 type='text'
                                 onChange={ (e) => setNoteText(e.target.value) }
                                 value={ noteText }
                                 name='note'
                                 placeholder={ `Add a message to ${ siteOwnerName }` }
                                 className='
                              w-full h-10 outline-none focus:outline-none focus
                              flex items-center relative justify-center select-none
                              border-solid border border-divider
                              rounded-lg flex-row-reverse p-3 dark:text-major-dark'
                                 style={ { marginTop: '6px' } }
                                 maxLength={ 500 }
                              />
                           </label>
                           <div style={ {
                              marginTop: '4px',
                              display: 'flex',
                              justifyContent: 'flex-end',
                           } }
                           // eslint-disable-next-line indent
                              className='text-placeholder dark:text-placeholder-dark'
                           >
                              <p
                                 style={ {
                                    fontSize: '14px',
                                    lineHeight: '18px',
                                    fontWeight: '400',
                                 } }
                              >{`${ noteText.length } of 500 characters used`}</p>
                           </div>
                        </div>
                     }
                  </div>
               </div>
            )
         }
      </div>
   )
}

const TopUp = ({
   amounts,
   selectedAmount,
   onSelectAmount,
   isFetching,
   balance,
   yourBalance,
   isAddProcessingFees,
   onChangeAddProcessingFees,
}) => {
   let afterTopUpBalance = balance;
   if(selectedAmount && selectedAmount.amount) {
      if(isAddProcessingFees) {
         afterTopUpBalance = balance + selectedAmount.amount;
      } else {
         afterTopUpBalance = balance + (selectedAmount.amount * 0.9);
      }
   }
   if(isFetching) {
      return <PageLoader customStyle='h-full w-full my-10' withLoader loaderColor='primary' />
   }
   return (
      <>
         {yourBalance}
         <div className='flex flex-col mt-7 select-none'>
            <span
               className='text-center text-sm text-secondary font-medium dark:text-secondary-dark '
               style={ { lineHeight: '16px' } }
            >
               Choose the amount to top up your wallet.
            </span>
            <div className='flex flex-wrap mt-6 mb-2 px-5'>
               {
                  amounts.map((amount, index) => {
                     return (
                        <AmountChip
                           amount={ amount.amount }
                           key={ index.toString() }
                           selected={ selectedAmount?.id === amount.id }
                           onSelectAmount={ () => onSelectAmount(amount) }
                           margin='4px'
                           width='calc(50% - 8px)'
                        />
                     )
                  })
               }
            </div>
            <div className='w-full h-12 flex items-center px-3 py-2 mt-4'>
               <Switch
                  label='Add processing fees to my top up amount'
                  name='add_processing_fees_to_my_top_up_amount'
                  togglePosition='right'
                  onChange={ onChangeAddProcessingFees }
                  checked={ isAddProcessingFees }
                  noHorizontalMargin={ false }
                  className='w-full '
                  isBlackandWhite
               />
            </div>
            <div className='flex justify-center w-full mt-6 mb-3 px-4'>
               <span className='text-sm text-secondary text-center dark:text-secondary-dark '>
                  {`Processing fees will be `}
                  <span className='font-medium'>{isAddProcessingFees ? 'added' : 'included'}</span>
                  {` ${ isAddProcessingFees ? 'to' : 'in' } your top up amount.`}
                  <span className='font-medium'>
                     {` Your balance after top up will be ${ numberFormatter.withDots(afterTopUpBalance) + ' USD' }.`}
                  </span>
               </span>
            </div>
         </div>
      </>
   )
}


WalletModal.propTypes = {
   onModalClose: PropTypes.func,
   selectedAmount: PropTypes.any,
   type: PropTypes.string,
   onSelectAmount: PropTypes.func,
   onClickButton: PropTypes.func,
   amounts: PropTypes.array,
   amountSettings: PropTypes.object,
   isMobile: PropTypes.bool,
   height: PropTypes.number,
   isFetching: PropTypes.bool,
   balance: PropTypes.number,
   topUpMyWallet: PropTypes.func,
   isFetchingWallet: PropTypes.bool,
   walletData: PropTypes.array,
   animate: PropTypes.string,
   hasPageHeader: PropTypes.bool,
   isProgressing: PropTypes.bool,
   isNotEnoughtBalance: PropTypes.bool,
   goToWalletTopUp: PropTypes.func,
   modalPosition: PropTypes.string,
   chatSendTextPrice: PropTypes.number,
   memberUuid: PropTypes.string,
   adminScreenName: PropTypes.string,
   isAddProcessingFees: PropTypes.bool,
   onChangeAddProcessingFees: PropTypes.func,
   isFetchingBalance: PropTypes.bool,
   paymentButtonLoading: PropTypes.bool,
   extraInfo: PropTypes.object,
   setSendPurchase: PropTypes.func,
   tipContentData: PropTypes.object,
   siteOwnerName: PropTypes.string,
};
ChatMessage.propTypes = {
   type: PropTypes.string,
   chatSendTextPrice: PropTypes.number,
   chatTextMessageDontShowModal: PropTypes.bool,
   setChatTextMessageDontShowModal: PropTypes.func,
};

Tip.propTypes = {
   amounts: PropTypes.array,
   selectedAmount: PropTypes.any,
   onSelectAmount: PropTypes.func,
   isFetching: PropTypes.bool,
   topUpMyWallet: PropTypes.func,
   balance: PropTypes.number,
   extraInfo: PropTypes.object,
   amountSettings: PropTypes.object,
   setNoteText: PropTypes.func,
   noteText: PropTypes.string,
   siteOwnerName: PropTypes.string,
   setIsCustomTipError: PropTypes.func,
   setUseIsCustomTip: PropTypes.func,
   isCustomTipError: PropTypes.bool,
   useIsCustomTip: PropTypes.bool,
};

TopUp.propTypes = {
   amounts: PropTypes.array,
   selectedAmount: PropTypes.any,
   onSelectAmount: PropTypes.func,
   isFetching: PropTypes.bool,
   balance: PropTypes.number,
   yourBalance: PropTypes.any,
   isAddProcessingFees: PropTypes.bool,
   onChangeAddProcessingFees: PropTypes.func,
};

WalletModal.defaultProps = {
   onModalClose: () => { },
   selectedAmount: 1,
   type: 'tip',
   hasPageHeader: true,
   modalPosition: 'full-screen',
}
export default React.memo(WalletModal);
